import React, { FunctionComponent } from 'react';

import { Text } from 'components/chroma';

import { ApiData } from 'scenes/common/types';
import { getApiData } from 'scenes/common/helpers/apiData';
import {
  HomeCoverSubSection,
  HomeFullPageProps,
  HomeFunnelState,
  HomeQuoteSubSection,
  ReferenceData,
} from 'scenes/home/types';
import {
  getExtras,
  hasBuildingCoverType,
  getSelectedBuildingExcess,
  hasContentsCoverType,
  getSelectedContentsExcess,
  getContentsSumInsured,
  getCoverType,
  hasUnspecifiedPortableContents,
  getUnspecifiedPortableContentsLimit,
  usingEstimatedSumInsured,
  getUnspecifiedPortableLimitRanges,
  getSpecifiedContentsHomeOnly,
  getSpecifiedPortableContentsAustraliaWide,
  getSpecifiedPortablesExcessAmount,
  getIsPortableContentsExtraSelected,
  getBuildingSumInsured,
} from 'scenes/home/selectors';
import SummaryCard from 'components/SummaryCard';
import { toCurrencyWithSign } from 'scenes/common/helpers/currency';
import { availableExtras } from 'scenes/home/helpers/OptionalExtras/availableExtras';

const toOptionalExtraDescription = (
  optionalExtra: string,
  funnelState: HomeFunnelState
): string =>
  availableExtras[getCoverType(funnelState)].find(
    (extra) => extra.code === optionalExtra
  )?.label || optionalExtra;

const getUnspecifiedPortableDescription = (funnelState: HomeFunnelState) => {
  const unspecifiedPortable = getUnspecifiedPortableContentsLimit(funnelState);
  return (
    getUnspecifiedPortableLimitRanges(funnelState).find(
      (range) => range.value === unspecifiedPortable
    )?.label ?? unspecifiedPortable
  );
};

const getSpecifiedContentDescription = (
  category: string,
  referenceData: ApiData<ReferenceData>
) =>
  getApiData(referenceData).specifiedPortableCategory.find(
    (type) => type.value === category
  )?.label ?? category;

export const CoverDetailsSummary: FunctionComponent<Pick<
  HomeFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditCoverDetails' | 'referenceData'
>> = ({
  funnelState,
  onEditQuoteDetails,
  onEditCoverDetails,
  referenceData,
}) => {
  const extras = [
    ...getExtras(funnelState),
    ...(hasContentsCoverType(funnelState) &&
    getIsPortableContentsExtraSelected(funnelState)
      ? ['Portable contents']
      : []),
  ];
  const specifiedContentsHomeOnly = getSpecifiedContentsHomeOnly(funnelState);
  const specifiedPortableContentsAustraliaWide = getSpecifiedPortableContentsAustraliaWide(
    funnelState
  );

  return (
    <>
      {hasBuildingCoverType(funnelState) && (
        <SummaryCard
          heading="Building Excess"
          onEditClick={() =>
            onEditCoverDetails(HomeCoverSubSection.BuildingExcess)
          }
        >
          <Text>{`${toCurrencyWithSign(
            getSelectedBuildingExcess(funnelState).amount
          )}`}</Text>
        </SummaryCard>
      )}
      {hasContentsCoverType(funnelState) && (
        <>
          <SummaryCard
            heading="Contents Excess"
            onEditClick={() =>
              onEditCoverDetails(HomeCoverSubSection.ContentsExcess)
            }
          >
            <Text>{`${toCurrencyWithSign(
              getSelectedContentsExcess(funnelState).amount
            )}`}</Text>
          </SummaryCard>
          {getIsPortableContentsExtraSelected(funnelState) && (
            <SummaryCard
              heading="Portable Contents"
              onEditClick={() =>
                onEditCoverDetails(HomeCoverSubSection.PortableContents)
              }
            >
              <Text>{`${toCurrencyWithSign(
                getSpecifiedPortablesExcessAmount(funnelState)
              )}`}</Text>
            </SummaryCard>
          )}
        </>
      )}
      {hasBuildingCoverType(funnelState) && (
        <SummaryCard
          heading="Building sum insured"
          onEditClick={() =>
            usingEstimatedSumInsured(funnelState)
              ? onEditQuoteDetails(HomeQuoteSubSection.BuildingSumInsuredChoice)
              : onEditQuoteDetails(HomeQuoteSubSection.BuildingAgreedSumInsured)
          }
        >
          <Text>{toCurrencyWithSign(getBuildingSumInsured(funnelState))}</Text>
        </SummaryCard>
      )}
      {hasContentsCoverType(funnelState) && (
        <SummaryCard
          heading="Contents sum insured"
          onEditClick={() =>
            onEditQuoteDetails(HomeQuoteSubSection.ContentsSumInsured)
          }
        >
          <Text>{`${toCurrencyWithSign(
            getContentsSumInsured(funnelState)
          )}`}</Text>
        </SummaryCard>
      )}
      <SummaryCard
        heading="Optional extras"
        onEditClick={() =>
          onEditCoverDetails(HomeCoverSubSection.OptionalExtras)
        }
      >
        {extras.length > 0 ? (
          extras.map((optionalExtra) => (
            <Text key={optionalExtra}>
              {toOptionalExtraDescription(optionalExtra, funnelState)}
            </Text>
          ))
        ) : (
          <Text>None</Text>
        )}
      </SummaryCard>
      {hasContentsCoverType(funnelState) && (
        <>
          <SummaryCard
            heading="Specified portables (part of Portable contents optional extra)"
            onEditClick={() =>
              onEditCoverDetails(HomeCoverSubSection.SpecifiedContents)
            }
          >
            {specifiedPortableContentsAustraliaWide.length > 0 ? (
              specifiedPortableContentsAustraliaWide.map(
                ({ category, description, itemValue }) => (
                  <React.Fragment key={description}>
                    <Text weight={600} margin={{ bottom: 0 }}>
                      {getSpecifiedContentDescription(category, referenceData)}{' '}
                      - {description}
                    </Text>
                    <Text>{toCurrencyWithSign(itemValue)}</Text>
                  </React.Fragment>
                )
              )
            ) : (
              <Text>None</Text>
            )}
          </SummaryCard>
          <SummaryCard
            heading="Unspecified portables (part of Portable contents optional extra)"
            onEditClick={() =>
              onEditCoverDetails(HomeCoverSubSection.UnspecifiedPortables)
            }
          >
            <Text>
              {hasUnspecifiedPortableContents(funnelState)
                ? getUnspecifiedPortableDescription(funnelState)
                : 'None'}
            </Text>
          </SummaryCard>
          <SummaryCard
            heading="Specified contents (within the home)"
            onEditClick={() =>
              onEditCoverDetails(HomeCoverSubSection.SpecifiedContents)
            }
          >
            {specifiedContentsHomeOnly.length > 0 ? (
              specifiedContentsHomeOnly.map(
                ({ category, description, itemValue }) => (
                  <React.Fragment key={description}>
                    <Text weight={600} margin={{ bottom: 0 }}>
                      {getSpecifiedContentDescription(category, referenceData)}{' '}
                      - {description}
                    </Text>
                    <Text>{toCurrencyWithSign(itemValue)}</Text>
                  </React.Fragment>
                )
              )
            ) : (
              <Text>None</Text>
            )}
          </SummaryCard>
        </>
      )}
    </>
  );
};

export default CoverDetailsSummary;
